/**
 * ScreeningPreferences — employer/admin page to configure per-field
 * importance and preferred answers for Deep Screening.
 *
 * Fields are grouped by their block (Source, Compensation, Notice &
 * joining, Location, etc.) matching the ScreeningProfileEditor layout.
 * Values are shape-aware:
 *   - yesno / select    → checkbox list of options
 *   - number            → min / max range inputs
 *   - text / textarea   → comma-separated preferred phrases
 *   - date / city       → no preferred-answer config (only importance)
 *
 * Backed by GET/PATCH /api/company/screening-prefs.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Save, ArrowLeft, Info } from "lucide-react";

export default function ScreeningPreferences() {
    const [fields, setFields] = useState(null);
    const [prefs, setPrefs] = useState({});
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);

    useEffect(() => {
        (async () => {
            try {
                const [fRes, pRes] = await Promise.all([
                    api.get("/screening-fields"),
                    api.get("/company/screening-prefs"),
                ]);
                setFields(fRes.data || { fields: [] });
                setPrefs(pRes.data?.screening_prefs || {});
            } catch (err) {
                toast.error(formatApiError(err));
            } finally { setLoading(false); }
        })();
    }, []);

    const blocks = useMemo(() => {
        const groups = {};
        (fields?.fields || []).forEach((f) => {
            (groups[f.block] = groups[f.block] || []).push(f);
        });
        return groups;
    }, [fields]);

    const updateField = (key, patch) => {
        setPrefs((p) => ({ ...p, [key]: { ...(p[key] || {}), ...patch } }));
        setDirty(true);
    };

    const togglePreferred = (key, option) => {
        setPrefs((p) => {
            const cur = new Set(p[key]?.preferred || []);
            cur.has(option) ? cur.delete(option) : cur.add(option);
            return { ...p, [key]: { ...(p[key] || {}), preferred: [...cur] } };
        });
        setDirty(true);
    };

    const clearField = (key) => {
        setPrefs((p) => {
            const next = { ...p };
            delete next[key];
            return next;
        });
        setDirty(true);
    };

    const save = async () => {
        setSaving(true);
        try {
            // Send only non-empty entries. Backend strips further.
            const payload = {};
            Object.entries(prefs).forEach(([k, v]) => {
                if (!v) return;
                payload[k] = {
                    importance: v.importance || "optional",
                    preferred: Array.isArray(v.preferred) ? v.preferred : undefined,
                    preferred_min: v.preferred_min ?? undefined,
                    preferred_max: v.preferred_max ?? undefined,
                };
            });
            const { data } = await api.patch("/company/screening-prefs", { screening_prefs: payload });
            setPrefs(data?.screening_prefs || {});
            setDirty(false);
            toast.success("Screening preferences saved");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setSaving(false); }
    };

    if (loading) return <div className="p-6 text-sm text-muted-foreground">Loading…</div>;

    return (
        <div className="max-w-5xl mx-auto p-4 sm:p-6 space-y-6" data-testid="screening-preferences-page">
            <div className="flex items-center gap-3 flex-wrap">
                <Link to="/candidates" className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground" data-testid="sp-back-link">
                    <ArrowLeft className="w-3.5 h-3.5" /> Back
                </Link>
                <h1 className="font-display font-bold text-2xl flex-1">Screening preferences</h1>
                <Button
                    onClick={save}
                    disabled={!dirty || saving}
                    className="rounded-none"
                    data-testid="sp-save-btn"
                >
                    <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save preferences"}
                </Button>
            </div>

            <div className="border border-border bg-secondary/40 p-4 flex gap-3 items-start">
                <Info className="w-4 h-4 mt-0.5 text-foreground/60 shrink-0" />
                <div className="text-sm text-foreground/80 space-y-1">
                    <p><b>Important</b> fields render an amber highlight in the Deep-Screening editor when still empty — helps recruiters spot gaps at a glance.</p>
                    <p><b>Preferred answers</b> render a green border when the candidate&apos;s answer matches; non-matching answers get a red border. Nothing is validated — this is a silent visual cue for reviewers.</p>
                </div>
            </div>

            {Object.entries(blocks).map(([block, list]) => (
                <div key={block} className="border border-border bg-white">
                    <div className="px-4 py-3 border-b border-border bg-secondary/40">
                        <h2 className="font-display font-bold text-base">{block}</h2>
                    </div>
                    <div className="p-4 space-y-4">
                        {list.map((f) => (
                            <FieldPrefRow
                                key={f.key}
                                field={f}
                                pref={prefs[f.key] || {}}
                                onUpdate={(patch) => updateField(f.key, patch)}
                                onTogglePreferred={(opt) => togglePreferred(f.key, opt)}
                                onClear={() => clearField(f.key)}
                            />
                        ))}
                    </div>
                </div>
            ))}

            <div className="sticky bottom-4 flex justify-end">
                <Button
                    onClick={save}
                    disabled={!dirty || saving}
                    className="rounded-none shadow-lg"
                    data-testid="sp-save-btn-bottom"
                >
                    <Save className="w-4 h-4 mr-1" /> {saving ? "Saving…" : "Save preferences"}
                </Button>
            </div>
        </div>
    );
}

function FieldPrefRow({ field: f, pref, onUpdate, onTogglePreferred, onClear }) {
    const isImportant = pref.importance === "important";
    // yesno + select expose their options as checkboxes; other shapes fall back to text/number ranges
    let optionsUI = null;
    if (f.type === "yesno") {
        const opts = ["Yes", "No", "Not asked"];
        optionsUI = (
            <div className="flex gap-2 flex-wrap">
                {opts.map((o) => (
                    <label key={o} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 border border-border bg-white hover:bg-secondary/40 cursor-pointer" data-testid={`sp-pref-${f.key}-${o.replace(/\s+/g, "-")}`}>
                        <input
                            type="checkbox"
                            checked={(pref.preferred || []).includes(o)}
                            onChange={() => onTogglePreferred(o)}
                        />
                        <span>{o}</span>
                    </label>
                ))}
            </div>
        );
    } else if (f.type === "select") {
        const opts = (f.options || []).filter(Boolean);
        optionsUI = (
            <div className="flex gap-2 flex-wrap">
                {opts.map((o) => (
                    <label key={o} className="inline-flex items-center gap-1.5 text-xs px-2 py-1 border border-border bg-white hover:bg-secondary/40 cursor-pointer" data-testid={`sp-pref-${f.key}-${o.replace(/\s+/g, "-")}`}>
                        <input
                            type="checkbox"
                            checked={(pref.preferred || []).includes(o)}
                            onChange={() => onTogglePreferred(o)}
                        />
                        <span>{o}</span>
                    </label>
                ))}
            </div>
        );
    } else if (f.type === "number") {
        optionsUI = (
            <div className="flex gap-3 items-end flex-wrap">
                <div>
                    <Label className="text-[11px] text-muted-foreground">Preferred min</Label>
                    <Input
                        type="number"
                        value={pref.preferred_min ?? ""}
                        onChange={(e) => onUpdate({ preferred_min: e.target.value === "" ? null : Number(e.target.value) })}
                        className="rounded-none h-8 w-28 mt-1"
                        placeholder="—"
                        data-testid={`sp-pref-min-${f.key}`}
                    />
                </div>
                <div>
                    <Label className="text-[11px] text-muted-foreground">Preferred max</Label>
                    <Input
                        type="number"
                        value={pref.preferred_max ?? ""}
                        onChange={(e) => onUpdate({ preferred_max: e.target.value === "" ? null : Number(e.target.value) })}
                        className="rounded-none h-8 w-28 mt-1"
                        placeholder="—"
                        data-testid={`sp-pref-max-${f.key}`}
                    />
                </div>
            </div>
        );
    } else if (f.type === "text" || f.type === "textarea") {
        const csv = (pref.preferred || []).join(", ");
        optionsUI = (
            <div>
                <Input
                    type="text"
                    defaultValue={csv}
                    onBlur={(e) => {
                        const arr = e.target.value.split(",").map((s) => s.trim()).filter(Boolean);
                        onUpdate({ preferred: arr });
                    }}
                    className="rounded-none h-8"
                    placeholder="comma-separated preferred phrases (case-insensitive)"
                    data-testid={`sp-pref-csv-${f.key}`}
                />
            </div>
        );
    } else {
        optionsUI = <div className="text-xs text-muted-foreground italic">No preferred-answer config for {f.type} fields — importance only.</div>;
    }

    return (
        <div className="border-l-2 border-border pl-3 py-2" data-testid={`sp-pref-row-${f.key}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <div className="text-sm font-medium">{f.label}</div>
                    <div className="text-[11px] text-muted-foreground">{f.key} · {f.type}</div>
                </div>
                <div className="flex items-center gap-2">
                    <label className="inline-flex items-center gap-1.5 text-xs cursor-pointer">
                        <input
                            type="checkbox"
                            checked={isImportant}
                            onChange={(e) => onUpdate({ importance: e.target.checked ? "important" : "optional" })}
                            data-testid={`sp-pref-important-${f.key}`}
                        />
                        <span className={isImportant ? "text-amber-800 font-medium" : "text-muted-foreground"}>Mark as important</span>
                    </label>
                    <button
                        type="button"
                        onClick={onClear}
                        className="text-[11px] text-muted-foreground hover:text-rose-700 underline"
                        data-testid={`sp-pref-clear-${f.key}`}
                    >
                        Clear
                    </button>
                </div>
            </div>
            <div className="mt-2">
                {optionsUI}
            </div>
        </div>
    );
}
